import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "store/baseApi";

const allApi = createApi({
  reducerPath: "inventory",
  baseQuery: axiosBaseQuery(),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Card",
    "Topcard",
    "Client",
    "Location",
    "Dropdowns",
    "Inventory",
    "Caps",
    "CardCompleted",
    "CLIENT_COMPANY",
    "ClientJob",
    "Coordinator",
    "Delivery",
    "CLIENT_COMPANY_COMPLETED",
    "Users",
    "JobcardImages",
    "DispatchDetails",
    "DigitizerUsers",
  ], //refresh when it invalidates
  endpoints(build) {
    return {
      fetchDesignerJobcards: build.query({
        query: ({ page = 1, page_size, title, jobcard_id } = {}) => {
          const params = { page };
          if (page_size) {
            params.page_size = page_size;
          }
          if (title) {
            params.title = title;
          }
          if (jobcard_id) {
            params.jobcard_id = jobcard_id;
          }
          return {
            url: `/api/degitizer-jobcards/`,
            method: "GET",
            params,
          };
        },
        providesTags: (result = [], error, arg) => ["DesignerJobcards"],
      }),
      getJobcardImageDetail: build.query({
        query: (id) => {
          return {
            url: `/api/jobcard-image-detail/${id}/`,
            method: "GET",
          };
        },
        providesTags: (result, error, arg) => [
          { type: "JobcardImages", id: arg },
          "DesignerJobcards",
        ],
      }),
      fetchDispatchDetails: build.query({
        query: (jobcardId) => {
          return {
            url: `/api/dispatch-details/?jobcard=${jobcardId}`,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) => [
          { type: "DispatchDetails", id: arg },
          "DispatchDetails",
        ],
      }),
      fetchJobcardActivityLog: build.query({
        query: (jobcardId) => {
          return {
            url: `/api/jobcard-activity/${jobcardId}/`,
            method: "GET",
          };
        },
        providesTags: (result, error, arg) => [
          { type: "JobcardActivityLog", id: arg },
          "JobcardActivityLog",
        ],
      }),
      fetchInventoryActivityLog: build.query({
        query: (inventoryId) => {
          return {
            url: `/inventory/detail/${inventoryId}/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
          };
        },
        providesTags: (result, error, arg) => [
          { type: "InventoryActivityLog", id: arg },
          "InventoryActivityLog",
        ],
      }),
      fetchJobcards: build.query({
        query: ({
          page,
          current_stage,
          stage_filter,
          client_filter,
          jobid_filter,
          sort,
          created_by,
          date,
          embridary_placement_front_filter,
          page_size = 10,
        }) => {
          let queryString = `/api/?page=${page}`;
          if (page_size) queryString += `&page_size=${page_size}`;
          if (client_filter) queryString += client_filter;
          if (stage_filter) queryString += stage_filter;
          if (jobid_filter) queryString += jobid_filter;
          if (created_by) queryString += `&created_by=${created_by}`;
          if (date) queryString += `&date=${date}`;
          if (embridary_placement_front_filter && String(embridary_placement_front_filter).trim() !== "") {
            queryString += `&embridary_placement_front=${encodeURIComponent(String(embridary_placement_front_filter).trim())}`;
          }
          queryString += `&sort=${sort}`;
          return {
            url: queryString,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.results?.length
            ? [
              ...result?.results?.map(({ id }) => ({ type: "Card", id })),
              "Card",
            ]
            : ["Card"],
      }),
      fetchJobcardsCompleted: build.query({
        query: ({ page, current_stage, client_filter, jobid_filter, sort, embridary_placement_front_filter_completed, page_size = 10 }) => {
          let queryString = `/api/?page=${page}&current_stage=completed${client_filter}`;
          if (page_size) queryString += `&page_size=${page_size}`;
          if (jobid_filter) queryString += jobid_filter;
          if (embridary_placement_front_filter_completed && String(embridary_placement_front_filter_completed).trim() !== "") {
            queryString += `&embridary_placement_front=${encodeURIComponent(String(embridary_placement_front_filter_completed).trim())}`;
          }
          queryString += `&sort=${sort}`;
          return {
            url: queryString,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.results?.length
            ? [
              ...result?.results?.map(({ id }) => ({
                type: "CardCompleted",
                id,
              })),
              "CardCompleted",
            ]
            : ["CardCompleted"],
      }),
      fetchTopCardCount: build.query({
        query: () => {
          return {
            url: `/api/card-count/`,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result.length
            ? [...result.map(({ id }) => ({ type: "Topcard", id })), "Topcard"]
            : ["Topcard"],
      }),

      fetchCoordinatorsJob: build.query({
        query: () => {
          return {
            url: `/api/co-ordinators-job/`,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result.length
            ? [
              ...result.map(({ id }) => ({ type: "Coordinator", id })),
              "Coordinator",
            ]
            : ["Coordinator"],
      }),
      fetchDeliveryDateJob: build.query({
        query: () => {
          return {
            url: `/api/dilevery-date-job/`,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result.length
            ? [
              ...result.map(({ id }) => ({ type: "Delivery", id })),
              "Delivery",
            ]
            : ["Delivery"],
      }),
      fetchClientJob: build.query({
        query: () => {
          return {
            url: `/api/client-job/`,
            method: "GET",
          };
        },
        providesTags: (result = [], error, arg) =>
          result.length
            ? [
              ...result.map(({ id }) => ({ type: "ClientJob", id })),
              "ClientJob",
            ]
            : ["ClientJob"],
      }),

      getJobcard: build.query({
        query: (id) => {
          return {
            url: `/api/detail/${id}`,
            method: "GET",
          };
        },
      }),
      createJobcard: build.mutation({
        query: (createJobcardData) => {
          const { ...data } = createJobcardData;

          var formdata = new FormData();
          Object.keys(data).forEach((form_key) => {
            const value = data[form_key];
            if (value === undefined || value === null || value === "") {
              // skip empty values
              return;
            }
            // Handle File inputs
            if (value instanceof File || (value && value.target && value.target.files)) {
              const file = value instanceof File ? value : value.target.files?.[0];
              if (file) formdata.append(form_key, file);
              return;
            }
            // Booleans as strings
            if (typeof value === "boolean") {
              formdata.append(form_key, String(value));
              return;
            }
            if (Array.isArray(value) && form_key === "assigned_digitizers") {
              value.forEach(v => formdata.append(form_key, v));
              return;
            }
            // Plain objects/arrays as JSON
            if (typeof value === "object") {
              formdata.append(form_key, JSON.stringify(value));
              return;
            }
            // Primitives
            formdata.append(form_key, value);
          });
          return {
            url: `/api/addjobcard`,
            method: "POST",
            body: formdata,
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "CLIENT_COMPANY" },
          { type: "Card", id: arg.id },
          { type: "Topcard" },
        ],
      }),
      createJobcardVersion: build.mutation({
        query: (jobcardId) => {
          return {
            url: `/api/create-jobcard-version/${jobcardId}/`,
            method: "POST",
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Card" },
          { type: "Topcard" },
          "DesignerJobcards",
        ],
      }),
      updateJobcard: build.mutation({
        query: (updateJobcardData) => {
          const { id, ...data } = updateJobcardData;
          var formdata = new FormData();
          // Fields where empty string should explicitly clear the value on backend
          const embroideryPlacementFields = new Set([
            "embr_Front",
            "embr_Back",
            "embr_Left_Middle",
            "embr_Right_Middle",
            "embr_Visor",
            "embr_Velcro",
            "embr_Visor_Square",
            "embr_One_Side_Emb",
          ]);
          Object.keys(data).forEach((form_key) => {
            const value = data[form_key];
            // Allow empty string for embroidery placement fields to clear them
            if (value === undefined || value === null) {
              return;
            }
            if (value === "" && !embroideryPlacementFields.has(form_key)) {
              return;
            }
            if (value instanceof File || (value && value.target && value.target.files)) {
              const file = value instanceof File ? value : value.target.files?.[0];
              if (file) formdata.append(form_key, file);
              return;
            }
            if (typeof value === "boolean") {
              formdata.append(form_key, String(value));
              return;
            }
            if (Array.isArray(value) && form_key === "assigned_digitizers") {
              value.forEach(v => formdata.append(form_key, v));
              return;
            }
            if (typeof value === "object") {
              formdata.append(form_key, JSON.stringify(value));
              return;
            }
            formdata.append(form_key, value);
          });
          return {
            url: `/api/update/${id}`,
            method: "PATCH",
            body: formdata,
          };
        },

        invalidatesTags: (result, error, arg) => [
          { type: "CLIENT_COMPANY" },
          { type: "Topcard" },
          { type: "CardCompleted" },
          { type: "Card", id: arg.id },
          "Card", // Invalidate all Card queries to refresh lists when forwarding
        ],
      }),
      uploadJobcardImages: build.mutation({
        query: (uploadData) => {
          const { job_card_id, is_digitizer_image, is_degitizer_image, is_embroidary_image, metadata, files } = uploadData;
          var formdata = new FormData();
          formdata.append("job_card", job_card_id);
          // Backend expects `is_degitizer_image` (note spelling)
          formdata.append("is_degitizer_image", String(Boolean(is_degitizer_image ?? is_digitizer_image)));
          // Backend expects `is_embroidary_image`
          formdata.append("is_embroidary_image", String(Boolean(is_embroidary_image)));
          formdata.append("metadata", JSON.stringify(metadata));

          // Append files with explicit filename to ensure backend can match them correctly
          files.forEach((file) => {
            // The third parameter explicitly sets the filename for FormData
            // This ensures the backend receives the file with the correct name for matching
            formdata.append("files", file, file.name);
          });

          return {
            url: `/api/upload-jobcard-images/`,
            method: "POST",
            body: formdata,
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Card" },
          { type: "DesignerJobcards" },
          { type: "JobcardImages", id: arg.job_card_id },
        ],
      }),
      updateJobcardImages: build.mutation({
        query: (uploadData) => {
          const { metadata, files } = uploadData;
          var formdata = new FormData();
          // metadata should be an array of objects with: id (required), filename (required), side, created_by
          formdata.append("metadata", JSON.stringify(metadata));
          files.forEach((file) => {
            formdata.append("files", file);
          });
          return {
            url: `/api/degitizer-images/update/`,
            method: "PATCH",
            body: formdata,
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Card" },
          { type: "DesignerJobcards" },
        ],
      }),
      updateSingleJobcardImage: build.mutation({
        query: (uploadData) => {
          const { id, degitizer_image } = uploadData;
          var formdata = new FormData();
          formdata.append("degitizer_image", degitizer_image);
          return {
            url: `/api/jobcard-images/${id}/`,
            method: "PUT",
            body: formdata,
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Card" },
          { type: "DesignerJobcards" },
          { type: "JobcardImages" },
        ],
      }),
      deleteJobcardImage: build.mutation({
        query: (id) => ({
          url: `/api/jobcard-images/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: (result, error, arg, meta) => {
          // Get jobcard ID from the response or need to fetch it
          // For now, invalidate all jobcard images and DesignerJobcards
          return [
            { type: "Card" },
            { type: "DesignerJobcards" },
            { type: "JobcardImages" },
          ];
        },
      }),
      // ===== Accountant Invoice APIs =====
      getJobcardInvoices: build.query({
        query: (jobCardId) => {
          return {
            url: `/api/invoices/${jobCardId}/`,
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
      }),
      uploadJobcardInvoices: build.mutation({
        query: (payload) => {
          const { job_card_id, files } = payload;
          const formdata = new FormData();
          formdata.append("job_card", job_card_id);
          files.forEach((file) => formdata.append("files", file));
          return {
            url: `/api/invoices/`,
            method: "POST",
            body: formdata,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Card", id: arg.job_card_id },
        ],
      }),
      deleteJobcardInvoice: build.mutation({
        query: (id) => ({
          url: `/api/invoices/detail/${id}/`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }),
      }),
      getClients: build.query({
        query: () => {
          return {
            url: `/api/clients`,
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        providesTags: ["Client"],
      }),
      fetchUsers: build.query({
        query: () => {
          return {
            url: `/api/users/`,
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        providesTags: ["Users"],
      }),
      getDropdowns: build.query({
        query: () => {
          return {
            url: `/api/array`,
            method: "GET",
          };
        },
        providesTags: ["Dropdowns"],
      }),
      fetchInventory: build.query({
        query: (query) => {
          const params = { ...query };
          // Ensure page and page_size are included if provided
          if (query?.page) {
            params.page = query.page;
          }
          if (query?.page_size) {
            params.page_size = query.page_size;
          }
          return {
            url: `/inventory/list/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
            params: params,
          };
        },
        providesTags: (result = [], error, arg) => {
          // Handle paginated response structure
          const data = result?.results || result;
          return data?.length
            ? [
              ...data.map(({ id }) => ({ type: "Inventory", id })),
              "Inventory",
            ]
            : ["Inventory"];
        },
      }),
      fetchInventoryFilterOptions: build.query({
        query: (query = {}) => {
          const params = { ...query };
          return {
            url: `/inventory/filter-options/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
            params,
          };
        },
      }),
      fetchInventoryClothTypes: build.query({
        query: (query = {}) => {
          const params = { ...query };
          return {
            url: `/inventory/cloth-types/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
            params,
          };
        },
      }),
      createInventory: build.mutation({
        query: (newItem) => {
          var formdata = new FormData();

          // Append all data fields to FormData
          Object.keys(newItem).forEach((form_key) => {
            const value = newItem[form_key];
            if (value !== null && value !== undefined && value !== '') {
              // Handle file uploads
              if (value instanceof File || (value && value.originFileObj)) {
                formdata.append(form_key, value instanceof File ? value : value.originFileObj);
              }
              // Handle arrays (like items array for multiple items)
              else if (Array.isArray(value)) {
                formdata.append(form_key, JSON.stringify(value));
              }
              // Handle objects
              else if (typeof value === 'object') {
                formdata.append(form_key, JSON.stringify(value));
              }
              else {
                formdata.append(form_key, value);
              }
            }
          });

          return {
            url: `/inventory/list/`,
            method: "POST",
            body: formdata,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Inventory" },
        ],
      }),
      updateInventory: build.mutation({
        query: (updatedItem) => {
          const { id, inventoryId, ...data } = updatedItem;
          const targetId = id || inventoryId;

          var formdata = new FormData();

          // Append all data fields to FormData
          Object.keys(data).forEach((form_key) => {
            const value = data[form_key];
            if (value !== null && value !== undefined && value !== '') {
              // Handle file uploads
              if (value instanceof File || (value && value.originFileObj)) {
                formdata.append(form_key, value instanceof File ? value : value.originFileObj);
              } else {
                formdata.append(form_key, value);
              }
            }
          });

          return {
            url: `/inventory/detail/${targetId}/`,
            method: "PUT",
            body: formdata,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Inventory", id: arg.id || arg.inventoryId },
        ],
      }),
      fetchCaps: build.query({
        query: ({ cap_name, page, cap_color, cloth_type, cap_material }) => {
          return {
            url: `/inventory/cap/?cap_type=${cap_name}&color=${cap_color}&cloth_type=${cloth_type}&cap_material=${cap_material}&page=${page}`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.results?.length
            ? [
              ...result.results.map(({ id }) => ({ type: "Caps", id })),
              "Caps",
            ]
            : ["Caps"],
      }),
      updateCap: build.mutation({
        query: (updatedItem) => {
          const { id, ...data } = updatedItem;
          var formdata = new FormData();
          Object.keys(data).map((form_key) =>
            formdata.append(form_key, data[form_key] || "")
          );
          return {
            url: `/inventory/cap/${id}`,
            method: "PUT",
            body: updatedItem,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Caps", id: arg.id },
          { type: "Inventory", id: arg.id },
        ],
      }),
      updateManufactureCap: build.mutation({
        query: (updatedItem) => {
          const { id, ...data } = updatedItem;
          var formdata = new FormData();
          Object.keys(data).map((form_key) =>
            formdata.append(form_key, data[form_key] || "")
          );
          return {
            url: `/inventory/manufactured_caps/${id}/`,
            method: "PUT",
            body: updatedItem,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Caps", id: arg.id },
          { type: "Card", id: arg.id },
        ],
      }),
      createCap: build.mutation({
        query: (createJobcardData) => {
          const { ...data } = createJobcardData;

          var formdata = new FormData();
          Object.keys(data).map((form_key) =>
            formdata.append(form_key, data[form_key] || "")
          );
          console.log(data);
          console.log(formdata);
          return {
            url: `/inventory/manufactured_caps/`,
            method: "POST",
            body: data,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              // ...formdata.getHeaders(),
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Inventory", id: arg.id },
          { type: "Card", id: arg.id },
          { type: "Caps", id: arg.id },
        ],
      }),
      fetchColors: build.query({
        query: ({ skip }) => {
          if (skip === undefined || skip === null) {
            console.log("if", skip);
          } else {
            console.log("else", skip);
            return {
              url: `/inventory/colors/?cap_type=${skip}`,
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("userToken")}`,
                Accept: "application/json",
              },
            };
          }
        },
        providesTags: (result = [], error, arg) =>
          result?.length
            ? [...result.map(({ id }) => ({ type: "Caps", id })), "Caps"]
            : ["Caps"],
      }),
      fetchManufacture_cap: build.query({
        query: ({ skip }) => {
          if (skip) {
            return {
              url: `/inventory/manufactured_caps/`,
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("userToken")}`,
                Accept: "application/json",
              },
            };
          }
        },
        providesTags: (result = [], error, arg) =>
          result?.length
            ? [...result.map(({ id }) => ({ type: "Caps", id })), "Caps"]
            : ["Caps"],
      }),
      getManufacture_cap: build.query({
        query: ({ id }) => {
          return {
            url: `/inventory/manufactured_caps/${id}/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.length
            ? [...result.map(({ id }) => ({ type: "Caps", id })), "Caps"]
            : ["Caps"],
      }),
      getCompanyNameFromJobcard: build.query({
        query: ({ id }) => {
          return {
            url: `/api/company-job/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.length
            ? [
              ...result.map(({ id }) => ({ type: "CLIENT_COMPANY", id })),
              "CLIENT_COMPANY",
            ]
            : ["CLIENT_COMPANY"],
      }),
      getCompanyNameFromJobcardCompleted: build.query({
        query: ({ id }) => {
          return {
            url: `/api/company-job-completed/`,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              Accept: "application/json",
            },
          };
        },
        providesTags: (result = [], error, arg) =>
          result?.length
            ? [
              ...result.map(({ id }) => ({
                type: "CLIENT_COMPANY_COMPLETED",
                id,
              })),
              "CLIENT_COMPANY_COMPLETED",
            ]
            : ["CLIENT_COMPANY_COMPLETED"],
      }),
      updateManufacturer: build.mutation({
        query: (updatedItem) => {
          const { id, ...data } = updatedItem;
          var formdata = new FormData();
          Object.keys(data).map((form_key) =>
            formdata.append(form_key, data[form_key] || "")
          );
          console.log(updatedItem);
          return {
            url: `/inventory/manufactured_caps/${id}/`,
            method: "PUT",
            body: updatedItem,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          };
        },
        invalidatesTags: (result, error, arg) => [
          { type: "Caps" },
          { type: "Inventory", id: arg.id },
          { type: "Card" },
        ],
      }),

      getDigitizerUsers: build.query({
        query: () => ({
          url: `/api/degitizer-users/`,
          method: "GET",
        }),
        providesTags: ["DigitizerUsers"],
      }),
      assignDigitizer: build.mutation({
        query: ({ id, assigned_degitizer }) => ({
          url: `/api/jobcards/${id}/assign-degitizer/`,
          method: "PATCH",
          body: { assigned_degitizer: assigned_degitizer ?? "" },
          headers: { "Content-Type": "application/json" },
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "Card", id: arg.id },
          "Card",
        ],
      }),

      // Inventory Reports
      fetchStockSummaryReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/stock-summary/`, method: "GET", params }),
      }),
      fetchLowStockReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/low-stock/`, method: "GET", params }),
      }),
      fetchProcurementReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/procurement/`, method: "GET", params }),
      }),
      fetchManufacturingUsageReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/manufacturing-usage/`, method: "GET", params }),
      }),
      fetchProcurementAuditReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/procurement-audit/`, method: "GET", params }),
      }),
      fetchInventoryValuationReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/valuation/`, method: "GET", params }),
      }),
      fetchStockSummaryDatewiseReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/stock-summary-datewise/`, method: "GET", params }),
      }),
      fetchUsageDatewiseReport: build.query({
        query: (params = {}) => ({ url: `/inventory/reports/usage-datewise/`, method: "GET", params }),
      }),

      // Machine endpoints moved to machineApi.js
    };
  },
});

export const {
  useFetchJobcardsQuery,
  useFetchJobcardsCompletedQuery,
  useFetchTopCardCountQuery,
  useGetJobcardQuery,
  useGetClientsQuery,
  useFetchUsersQuery,
  useGetDropdownsQuery,
  useCreateJobcardMutation,
  useCreateJobcardVersionMutation,
  useUpdateJobcardMutation,
  useFetchInventoryQuery,
  useFetchInventoryFilterOptionsQuery,
  useFetchInventoryClothTypesQuery,
  useCreateInventoryMutation,
  useUpdateInventoryMutation,
  useUpdateManufactureCapMutation,
  useFetchCapsQuery,
  useUpdateCapMutation,
  useCreateCapMutation,
  useFetchColorsQuery,
  useFetchManufacture_capQuery,
  useUpdateManufacturerMutation,
  useGetManufacture_capQuery,
  useGetCompanyNameFromJobcardQuery,
  useFetchClientJobQuery,
  useFetchCoordinatorsJobQuery,
  useFetchDeliveryDateJobQuery,
  useGetCompanyNameFromJobcardCompletedQuery,
  useUploadJobcardImagesMutation,
  useFetchDesignerJobcardsQuery,
  useGetJobcardImageDetailQuery,
  useFetchDispatchDetailsQuery,
  useUpdateJobcardImagesMutation,
  useUpdateSingleJobcardImageMutation,
  useDeleteJobcardImageMutation,
  useGetJobcardInvoicesQuery,
  useUploadJobcardInvoicesMutation,
  useDeleteJobcardInvoiceMutation,
  useFetchJobcardActivityLogQuery,
  useFetchInventoryActivityLogQuery,
  useGetDigitizerUsersQuery,
  useAssignDigitizerMutation,
  useLazyFetchStockSummaryReportQuery,
  useLazyFetchLowStockReportQuery,
  useLazyFetchProcurementReportQuery,
  useLazyFetchManufacturingUsageReportQuery,
  useLazyFetchProcurementAuditReportQuery,
  useLazyFetchInventoryValuationReportQuery,
  useLazyFetchStockSummaryDatewiseReportQuery,
  useLazyFetchUsageDatewiseReportQuery,
} = allApi;

export { allApi };